import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProjectIntakeForm from '../components/ProjectIntakeForm';
import ProjectList from '../components/ProjectList';
import InvoiceList from '../components/InvoiceList';
import ProjectStatus from '../components/ProjectStatus';
import PaymentGate from '../components/PaymentGate';
import { useAuth } from '../hooks/useAuth';

const API_URL = process.env.REACT_APP_API_URL || '';

const ClientPortal = ({ tab = 'dashboard' }) => {
  const [activeTab, setActiveTab] = useState(tab);
  const navigate = useNavigate();

  // Sync activeTab with prop when route changes
  useEffect(() => {
    setActiveTab(tab);
  }, [tab]);

  // Handle tab navigation with URL update
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const routes = {
      dashboard: '/client',
      upload: '/client/upload',
      projects: '/client/projects',
      invoices: '/client/invoices',
      messages: '/client/messages'
    };
    if (routes[newTab]) {
      navigate(routes[newTab]);
    }
  };

  return (
    <div className="client-portal">
      {/* Header */}
      <div className="portal-header">
        <h1>Client Portal</h1>
        <p>Manage your shop drawing projects</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleTabChange('dashboard')}
        >
          📊 Dashboard
        </button>
        <button
          className={`tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => handleTabChange('upload')}
        >
          📤 New Project
        </button>
        <button
          className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => handleTabChange('projects')}
        >
          📁 My Projects
        </button>
        <button
          className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => handleTabChange('invoices')}
        >
          💰 Invoices
        </button>
        <button
          className={`tab-btn ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => handleTabChange('messages')}
        >
          💬 Messages
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'dashboard' && <ClientDashboard onNavigate={handleTabChange} />}
        {activeTab === 'upload' && <ProjectIntakeForm />}
        {activeTab === 'projects' && <ClientProjectsView onNavigate={handleTabChange} />}
        {activeTab === 'invoices' && <InvoiceList />}
        {activeTab === 'messages' && <MessagesTab />}
      </div>
    </div>
  );
};

// Dashboard Component with better error handling
const ClientDashboard = ({ onNavigate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    activeProjects: 0,
    completedProjects: 0,
    pendingInvoices: 0,
    totalSpent: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);

      // If no token, use demo data
      if (!token) {
        setStats({
          activeProjects: 2,
          completedProjects: 5,
          pendingInvoices: 1,
          totalSpent: 12500
        });
        setRecentProjects([
          { id: 'PRJ-2026-0001', name: 'Warehouse Roof Replacement', status: 'in_progress', progress: 65 },
          { id: 'PRJ-2026-0002', name: 'Office Building Waterproofing', status: 'review', progress: 90 },
        ]);
        setLoading(false);
        return;
      }

      try {
        // Fetch projects with error handling
        let projects = [];
        try {
          const projectsRes = await fetch(`${API_URL}/api/projects`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (projectsRes.ok) {
            const projectsData = await projectsRes.json();
            projects = projectsData.projects || [];
          }
        } catch (e) {
          console.warn('Could not fetch projects:', e);
        }

        // Fetch invoices with error handling
        let invoices = [];
        try {
          const invoicesRes = await fetch(`${API_URL}/api/invoices`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (invoicesRes.ok) {
            const invoicesData = await invoicesRes.json();
            invoices = invoicesData.invoices || [];
          }
        } catch (e) {
          console.warn('Could not fetch invoices:', e);
        }

        // Calculate stats
        const activeCount = projects.filter(p =>
          ['in_progress', 'intake', 'queued', 'review', 'revision'].includes(p.status)
        ).length;

        const completedCount = projects.filter(p => p.status === 'completed').length;

        const pendingInvoiceCount = invoices.filter(i =>
          ['sent', 'viewed', 'partial', 'overdue'].includes(i.status)
        ).length;

        const totalSpent = invoices
          .filter(i => i.status === 'paid')
          .reduce((sum, i) => sum + (i.amount_paid || 0), 0);

        setStats({
          activeProjects: activeCount,
          completedProjects: completedCount,
          pendingInvoices: pendingInvoiceCount,
          totalSpent: totalSpent
        });

        // Get 5 most recent projects
        const sortedProjects = projects
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5)
          .map(p => ({
            id: p.project_number,
            name: p.name,
            status: p.status,
            progress: p.progress || 0
          }));

        setRecentProjects(sortedProjects);

      } catch (err) {
        console.error('Dashboard fetch error:', err);
        // Show dashboard with empty state rather than error
        setStats({
          activeProjects: 0,
          completedProjects: 0,
          pendingInvoices: 0,
          totalSpent: 0
        });
        setRecentProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="loader"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const getStatusLabel = (status) => {
    const labels = {
      'awaiting_funds': 'Awaiting Funds',
      'intake': 'Intake',
      'queued': 'Queued',
      'in_progress': 'In Progress',
      'review': 'Review',
      'revision': 'Revision',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  return (
    <div className="dashboard">
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📁</div>
          <div className="stat-info">
            <span className="stat-value">{stats.activeProjects}</span>
            <span className="stat-label">Active Projects</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.completedProjects}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
        <div className={`stat-card ${stats.pendingInvoices > 0 ? 'warning' : ''}`}>
          <div className="stat-icon">📋</div>
          <div className="stat-info">
            <span className="stat-value">{stats.pendingInvoices}</span>
            <span className="stat-label">Pending Invoices</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💵</div>
          <div className="stat-info">
            <span className="stat-value">${stats.totalSpent.toLocaleString()}</span>
            <span className="stat-label">Total Spent</span>
          </div>
        </div>
      </div>

      {/* Recent Projects */}
      <div className="section">
        <h2>Recent Projects</h2>
        {recentProjects.length === 0 ? (
          <div className="empty-state">
            <p>No projects yet. Start your first project!</p>
            <button className="action-btn primary" onClick={() => onNavigate && onNavigate('upload')}>
              Start New Project
            </button>
          </div>
        ) : (
          <div className="project-cards">
            {recentProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-header">
                  <span className="project-id">{project.id}</span>
                  <span className={`status-badge ${project.status}`}>
                    {getStatusLabel(project.status)}
                  </span>
                </div>
                <h3>{project.name}</h3>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${project.progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{project.progress}% Complete</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="section">
        <h2>Quick Actions</h2>
        <div className="quick-actions">
          <button className="action-btn primary" onClick={() => onNavigate && onNavigate('upload')}>
            📤 Start New Project
          </button>
          <button className="action-btn" onClick={() => onNavigate && onNavigate('invoices')}>
            💰 View All Invoices
          </button>
          <button className="action-btn" onClick={() => onNavigate && onNavigate('projects')}>
            📁 View All Projects
          </button>
          <button className="action-btn" onClick={() => onNavigate && onNavigate('messages')}>
            💬 Send Message
          </button>
        </div>
      </div>
    </div>
  );
};

// Enhanced Projects View with filtering and payment capability
const ClientProjectsView = ({ onNavigate }) => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewProject, setViewProject] = useState(null);
  const [payProject, setPayProject] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, [token]);

  const fetchProjects = async () => {
    setLoading(true);

    // Demo data if no token
    if (!token) {
      setProjects([
        { id: 1, project_number: 'PRJ-2026-0001', name: 'Warehouse Roof', status: 'in_progress', progress: 65, due_date: '2026-02-15', quoted_amount: 2500, paid: false },
        { id: 2, project_number: 'PRJ-2026-0002', name: 'Office Waterproofing', status: 'review', progress: 90, due_date: '2026-02-10', quoted_amount: 3200, paid: true },
        { id: 3, project_number: 'PRJ-2025-0015', name: 'School Gymnasium', status: 'completed', progress: 100, due_date: '2025-12-20', quoted_amount: 4500, paid: true },
        { id: 4, project_number: 'PRJ-2026-0003', name: 'Hospital Wing B', status: 'intake', progress: 10, due_date: '2026-03-01', quoted_amount: 8000, paid: false },
      ]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (e) {
      console.error('Error fetching projects:', e);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = (project) => {
    // Open the real Stripe payment flow (PaymentGate creates the intent server-side)
    setPayProject(project);
  };

  const filteredProjects = projects.filter(p => {
    const matchesFilter = filter === 'all' ||
      (filter === 'active' && ['in_progress', 'review', 'intake', 'queued'].includes(p.status)) ||
      (filter === 'completed' && p.status === 'completed') ||
      (filter === 'drafts' && p.status === 'draft') ||
      (filter === 'payment_due' && !p.paid && p.status !== 'draft');

    const matchesSearch = !searchTerm ||
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.project_number?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const getStatusLabel = (status) => {
    const labels = {
      'draft': 'Draft',
      'awaiting_funds': 'Awaiting Payment',
      'intake': 'Intake',
      'queued': 'Queued',
      'in_progress': 'In Progress',
      'review': 'Under Review',
      'revision': 'Revision',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  if (loading) {
    return <div className="loading"><div className="loader"></div><p>Loading projects...</p></div>;
  }

  return (
    <div className="projects-view">
      <div className="projects-header">
        <h2>My Projects</h2>
        <button className="btn primary" onClick={() => onNavigate('upload')}>
          + New Project
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-buttons">
          <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            All ({projects.length})
          </button>
          <button className={`filter-btn ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>
            Active ({projects.filter(p => ['in_progress', 'review', 'intake', 'queued'].includes(p.status)).length})
          </button>
          <button className={`filter-btn ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>
            Completed ({projects.filter(p => p.status === 'completed').length})
          </button>
          <button className={`filter-btn ${filter === 'payment_due' ? 'active' : ''}`} onClick={() => setFilter('payment_due')}>
            Payment Due ({projects.filter(p => !p.paid && p.status !== 'draft').length})
          </button>
        </div>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Projects List */}
      <div className="projects-table">
        <table>
          <thead>
            <tr>
              <th>Project #</th>
              <th>Name</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Due Date</th>
              <th>Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-row">
                  No projects found. <button className="link-btn" onClick={() => onNavigate('upload')}>Create your first project</button>
                </td>
              </tr>
            ) : (
              filteredProjects.map(project => (
                <tr key={project.id}>
                  <td><code>{project.project_number}</code></td>
                  <td>{project.name}</td>
                  <td>
                    <span className={`status-badge ${project.status}`}>
                      {getStatusLabel(project.status)}
                    </span>
                  </td>
                  <td>
                    <div className="progress-mini">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${project.progress}%` }}></div>
                      </div>
                      <span>{project.progress}%</span>
                    </div>
                  </td>
                  <td>{project.due_date || '-'}</td>
                  <td>${(project.quoted_amount || 0).toLocaleString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn small" onClick={() => setViewProject(project)}>View</button>
                      {!project.paid && project.status !== 'draft' && (
                        <button
                          className="btn small primary"
                          onClick={() => handlePayment(project)}
                        >
                          Pay Now
                        </button>
                      )}
                      {project.status === 'completed' && (
                        <button className="btn small" disabled title="Coming soon">Download</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Project Detail Modal */}
      {viewProject && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px', zIndex: 1000 }}
          onClick={() => setViewProject(null)}
        >
          <div style={{ maxWidth: '720px', width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
              <button className="btn small" onClick={() => setViewProject(null)}>Close</button>
            </div>
            <ProjectStatus projectId={viewProject.id} onPaymentSuccess={() => fetchProjects()} />
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {payProject && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px', zIndex: 1000 }}
        >
          <div style={{ maxWidth: '540px', width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <PaymentGate
              projectId={payProject.id}
              depositAmount={(payProject.quoted_amount || 0) * 0.5}
              pageFee={0}
              totalDue={(payProject.quoted_amount || 0) * 0.5}
              projectName={payProject.name}
              tier="standard"
              estimatedHours={Math.round((payProject.quoted_amount || 0) / 65)}
              hourlyRate={65}
              onPaymentSuccess={() => {
                setPayProject(null);
                fetchProjects();
              }}
              onCancel={() => setPayProject(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Messages Tab Component
const MessagesTab = () => {
  const { token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const composeRef = useRef(null);

  const handleNewMessage = () => {
    // Open the compose box: select the most recent conversation and focus the reply input
    if (!selectedConversation && messages.length > 0) {
      setSelectedConversation(messages[0]);
    }
    setTimeout(() => {
      if (composeRef.current) composeRef.current.focus();
    }, 0);
  };

  useEffect(() => {
    fetchMessages();
  }, [token]);

  const fetchMessages = async () => {
    setLoading(true);

    // Demo data - in production this would come from API
    const demoMessages = [
      {
        id: 1,
        subject: 'Project PRJ-2026-0001 Update',
        from: 'Shop Drawings Team',
        from_role: 'admin',
        preview: 'Your shop drawings are 65% complete. We have a few questions about the roof drain locations...',
        timestamp: '2026-01-12T10:30:00Z',
        unread: true,
        thread: [
          { id: 1, from: 'Shop Drawings Team', content: 'Your shop drawings are 65% complete. We have a few questions about the roof drain locations. Can you provide the mechanical drawings showing the RTU placement?', timestamp: '2026-01-12T10:30:00Z', isAdmin: true },
        ]
      },
      {
        id: 2,
        subject: 'Invoice #INV-2026-0012 Ready',
        from: 'Billing Department',
        from_role: 'admin',
        preview: 'Your invoice for the Hospital Wing B project is ready for review...',
        timestamp: '2026-01-11T14:15:00Z',
        unread: false,
        thread: [
          { id: 1, from: 'Billing Department', content: 'Your invoice for the Hospital Wing B project is ready for review. Total amount: $8,000.00. Payment is due within 15 days.', timestamp: '2026-01-11T14:15:00Z', isAdmin: true },
          { id: 2, from: user?.name || 'You', content: 'Thank you, I will process this today.', timestamp: '2026-01-11T15:00:00Z', isAdmin: false },
        ]
      },
      {
        id: 3,
        subject: 'Welcome to ShopDrawings.AI',
        from: 'Support Team',
        from_role: 'admin',
        preview: 'Welcome aboard! Here\'s how to get started with your first project...',
        timestamp: '2026-01-01T09:00:00Z',
        unread: false,
        thread: [
          { id: 1, from: 'Support Team', content: 'Welcome aboard! Here\'s how to get started with your first project:\n\n1. Click "New Project" to begin\n2. Upload your construction documents\n3. Select your service tier\n4. Make payment to start production\n\nWe typically complete drawings within 3-5 business days. Contact us anytime with questions!', timestamp: '2026-01-01T09:00:00Z', isAdmin: true },
        ]
      }
    ];

    setMessages(demoMessages);
    setLoading(false);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    // Add message to thread
    const updatedMessages = messages.map(m => {
      if (m.id === selectedConversation.id) {
        return {
          ...m,
          thread: [
            ...m.thread,
            {
              id: m.thread.length + 1,
              from: user?.name || 'You',
              content: newMessage,
              timestamp: new Date().toISOString(),
              isAdmin: false
            }
          ]
        };
      }
      return m;
    });

    setMessages(updatedMessages);
    setSelectedConversation({
      ...selectedConversation,
      thread: [
        ...selectedConversation.thread,
        {
          id: selectedConversation.thread.length + 1,
          from: user?.name || 'You',
          content: newMessage,
          timestamp: new Date().toISOString(),
          isAdmin: false
        }
      ]
    });
    setNewMessage('');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;

    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // Less than 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return <div className="loading"><div className="loader"></div><p>Loading messages...</p></div>;
  }

  return (
    <div className="messages-tab">
      <div className="messages-container">
        {/* Conversations List */}
        <div className="conversations-list">
          <div className="conversations-header">
            <h3>Messages</h3>
            <button className="btn small primary" onClick={handleNewMessage}>+ New Message</button>
          </div>
          <div className="conversations">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`conversation-item ${selectedConversation?.id === msg.id ? 'selected' : ''} ${msg.unread ? 'unread' : ''}`}
                onClick={() => setSelectedConversation(msg)}
              >
                <div className="conv-avatar">
                  {msg.from.charAt(0)}
                </div>
                <div className="conv-content">
                  <div className="conv-header">
                    <span className="conv-from">{msg.from}</span>
                    <span className="conv-time">{formatDate(msg.timestamp)}</span>
                  </div>
                  <div className="conv-subject">{msg.subject}</div>
                  <div className="conv-preview">{msg.preview}</div>
                </div>
                {msg.unread && <span className="unread-dot"></span>}
              </div>
            ))}
          </div>
        </div>

        {/* Message Thread */}
        <div className="message-thread">
          {selectedConversation ? (
            <>
              <div className="thread-header">
                <h3>{selectedConversation.subject}</h3>
              </div>
              <div className="thread-messages">
                {selectedConversation.thread.map(msg => (
                  <div key={msg.id} className={`message ${msg.isAdmin ? 'from-admin' : 'from-me'}`}>
                    <div className="message-header">
                      <span className="message-from">{msg.from}</span>
                      <span className="message-time">{formatDate(msg.timestamp)}</span>
                    </div>
                    <div className="message-content">{msg.content}</div>
                  </div>
                ))}
              </div>
              <form className="reply-form" onSubmit={sendMessage}>
                <textarea
                  ref={composeRef}
                  placeholder="Type your reply..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                />
                <button type="submit" className="btn primary" disabled={!newMessage.trim()}>
                  Send Reply
                </button>
              </form>
            </>
          ) : (
            <div className="no-conversation">
              <span>💬</span>
              <p>Select a conversation to view messages</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .messages-tab {
          padding: 0;
        }
        .messages-container {
          display: grid;
          grid-template-columns: 350px 1fr;
          height: calc(100vh - 250px);
          min-height: 500px;
          background: var(--bg-secondary);
          border-radius: 8px;
          overflow: hidden;
        }
        .conversations-list {
          border-right: 1px solid var(--border-color, #334155);
          display: flex;
          flex-direction: column;
        }
        .conversations-header {
          padding: 16px;
          border-bottom: 1px solid var(--border-color, #334155);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .conversations-header h3 {
          margin: 0;
          font-size: 18px;
        }
        .conversations {
          flex: 1;
          overflow-y: auto;
        }
        .conversation-item {
          padding: 16px;
          border-bottom: 1px solid var(--border-color, #334155);
          cursor: pointer;
          display: flex;
          gap: 12px;
          position: relative;
        }
        .conversation-item:hover {
          background: var(--bg-tertiary);
        }
        .conversation-item.selected {
          background: var(--primary-light, rgba(37, 99, 235, 0.1));
        }
        .conversation-item.unread {
          background: var(--bg-tertiary);
        }
        .conv-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--primary);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          flex-shrink: 0;
        }
        .conv-content {
          flex: 1;
          min-width: 0;
        }
        .conv-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
        }
        .conv-from {
          font-weight: 600;
          font-size: 14px;
        }
        .conv-time {
          font-size: 12px;
          color: var(--text-muted);
        }
        .conv-subject {
          font-size: 14px;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .conv-preview {
          font-size: 13px;
          color: var(--text-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .unread-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--primary);
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
        }
        .message-thread {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .thread-header {
          padding: 16px 24px;
          border-bottom: 1px solid var(--border-color, #334155);
        }
        .thread-header h3 {
          margin: 0;
          font-size: 16px;
        }
        .thread-messages {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .message {
          max-width: 80%;
          padding: 12px 16px;
          border-radius: 12px;
        }
        .message.from-admin {
          background: var(--bg-tertiary);
          align-self: flex-start;
        }
        .message.from-me {
          background: var(--primary);
          color: white;
          align-self: flex-end;
        }
        .message-header {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 8px;
          font-size: 12px;
        }
        .message.from-me .message-header {
          color: rgba(255,255,255,0.8);
        }
        .message-from {
          font-weight: 600;
        }
        .message-content {
          white-space: pre-wrap;
          line-height: 1.5;
        }
        .reply-form {
          padding: 16px 24px;
          border-top: 1px solid var(--border-color, #334155);
          display: flex;
          gap: 12px;
        }
        .reply-form textarea {
          flex: 1;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid var(--border-color, #334155);
          background: var(--bg-primary);
          color: var(--text-primary);
          resize: none;
        }
        .reply-form button {
          align-self: flex-end;
        }
        .no-conversation {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
        }
        .no-conversation span {
          font-size: 48px;
          margin-bottom: 16px;
        }
        .projects-view {
          padding: 0;
        }
        .projects-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        .projects-header h2 {
          margin: 0;
        }
        .filters-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .filter-buttons {
          display: flex;
          gap: 8px;
        }
        .filter-btn {
          padding: 8px 16px;
          border-radius: 20px;
          border: 1px solid var(--border-color, #334155);
          background: transparent;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 14px;
        }
        .filter-btn:hover {
          background: var(--bg-tertiary);
        }
        .filter-btn.active {
          background: var(--primary);
          color: white;
          border-color: var(--primary);
        }
        .search-bar input {
          padding: 8px 16px;
          border-radius: 8px;
          border: 1px solid var(--border-color, #334155);
          background: var(--bg-secondary);
          color: var(--text-primary);
          width: 250px;
        }
        .projects-table {
          background: var(--bg-secondary);
          border-radius: 8px;
          overflow: hidden;
        }
        .projects-table table {
          width: 100%;
          border-collapse: collapse;
        }
        .projects-table th,
        .projects-table td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--border-color, #334155);
        }
        .projects-table th {
          background: var(--bg-tertiary);
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .projects-table code {
          font-family: monospace;
          background: var(--bg-tertiary);
          padding: 4px 8px;
          border-radius: 4px;
        }
        .progress-mini {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .progress-mini .progress-bar {
          width: 80px;
          height: 6px;
          background: var(--bg-tertiary);
          border-radius: 3px;
          overflow: hidden;
        }
        .progress-mini .progress-fill {
          height: 100%;
          background: var(--primary);
          border-radius: 3px;
        }
        .action-buttons {
          display: flex;
          gap: 8px;
        }
        .empty-row {
          text-align: center;
          padding: 48px !important;
          color: var(--text-muted);
        }
        .link-btn {
          background: none;
          border: none;
          color: var(--primary);
          cursor: pointer;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default ClientPortal;
